import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Grid from '@mui/system/Unstable_Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Alert,
  Stack,
  Drawer,
  Divider,
  Tooltip,
  CardHeader,
  Typography,
  CardActions,
  CardContent
} from '@mui/material';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { useTranslate } from 'src/locales';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { EntityLabelType } from 'src/services/utils/utils.types';
import { useSearchLabelsQuery } from 'src/services/utils/utils.service';
import { OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { toggleCandidateEditorSidesheet } from 'src/store/slices/candidate/candidateSlice';
import { generateCandidateCreateErrorMessage } from 'src/services/candidates/candidates.utils';
import { useGetOrganizationCandidateQuery, useCreateUnlinkedCandidateMutation } from 'src/services/candidates/candidates.service';

import FormProvider, { RHFCheckbox, RHFTextField, RHFAutocomplete } from 'src/components/hook-form';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose?: () => void;
  candidateId?: string | null;
}

export default function CandidateEditorSideSheet({ open, onClose = () => { }, candidateId = null }: Props) {

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.CANDIDATE
  });

  const candidateEditorState = useAppSelector((state) => state.candidate.candidateEditorState);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [labelSearchQuery, setLabelSearchQuery] = useState<string>('');

  const { data: candidate } = useGetOrganizationCandidateQuery(candidateId as string, {
    skip: !candidateId,
  });

  const { canAccess } = useFeatures();

  const [createLocalCandidate, { isLoading: createLocalCandidateLoading }] = useCreateUnlinkedCandidateMutation();

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelSearchQuery ?? undefined,
    type: EntityLabelType.CANDIDATE,
    page: 1,
    per_page: 10
  });

  const CandidateFormSchema = Yup.object().shape({
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    // A pasted address often brings a leading space; trim it rather than calling the address invalid.
    email: Yup.string().trim().email(t('validation.email_format')).nullable().optional(),
    phone_number: Yup.string().nullable(),
    country_code: Yup.string().nullable(),
    position_title: Yup.string().nullable(),
    create_invite: Yup.boolean().nullable(),
    special_information: Yup.string().nullable(),
    labels: Yup.array().of(Yup.mixed<{ value: string, label: string }>()).nullable(),
  });

  const defaultValues = useMemo(() => ({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    country_code: '',
    position_title: '',
    special_information: '',
    create_invite: false,
    labels: [],
  }), []);

  const methods = useForm({
    resolver: yupResolver(CandidateFormSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting: formSubmitting },
  } = methods;

  useEffect(() => {
    if (!open) return;

    // Currently unreachable: nothing opens this panel with a `candidateId` — every caller passes
    // null or omits it. Candidate edits happen on the detail page cards, `candidate-contact-card`
    // and `candidate-labels-card`. Kept so the panel stays correct if an edit caller is added.
    if (candidate) {

      reset({
        first_name: candidate.candidate_profile.first_name,
        last_name: candidate.candidate_profile.last_name,
        email: candidate.email,
        phone_number: candidate.phone_number,
        country_code: candidate.country_code,
        position_title: candidate.candidate_profile.position_title,
        special_information: candidate.special_information,
        labels: Array.isArray(candidate.labels) ? candidate.labels.map((label) => ({
          label: label.label,
          value: label.id as string,
        })) : [],
        create_invite: false
      });
    }
    else if (candidateEditorState.email && candidateEditorState.firstName && candidateEditorState.lastName) {
        reset({
          first_name: candidateEditorState.firstName,
          last_name: candidateEditorState.lastName,
          email: candidateEditorState.email,
          phone_number: '',
          country_code: '',
          position_title: '',
          special_information: '',
          labels: [],
          create_invite: false
        });
      } else {
        // Reopened with no prefill. Without this the previous contact's details stay on screen,
        // because the prefill `reset(values)` adopted them as RHF's defaults.
        customFields.resetFields();
        reset(defaultValues);
      }

    // `customFields.resetFields` is a stable callback; the `customFields` object itself is a new
    // reference every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidate, reset, defaultValues, candidateEditorState, customFields.resetFields]);

  const onSubmit = handleSubmit(async (data) => {

    if (formSubmitting) return;

    // Validate custom fields first
    const customFieldValidation = customFields.validate();
    if (!customFieldValidation.isValid) {
      return;
    }

    setErrorMessage(null);

    try {

      await createLocalCandidate({
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email ?? undefined,
          phone_number: data.phone_number as string,
          country_code: data.country_code as string,
          position_title: data.position_title as string,
          create_invite: data.create_invite as boolean,
          special_information: data.special_information as string,
          labels: Array.isArray(data.labels) ? data.labels.map((item) => ({
            id: item?.value as string,
          })) : [],
          custom_fields: customFields.getData()
        }
      }).unwrap();

      enqueueSnackbar(candidate ? t('candidates.api.update_unlinked_candidate.success') : t('candidates.api.create_unlinked_candidate.success'), { variant: 'success' });
      handleClose();
    } catch (error) {

      const errMsg = generateCandidateCreateErrorMessage(error);
      setErrorMessage(errMsg);
    }
  });

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setLabelSearchQuery(value);
    }, 500),
    [setLabelSearchQuery]
  );

  const handleClose = (e?: any, reason?: string) => {
    if (reason && reason === 'backdropClick') return;

    // `reset()` with no argument restores whatever the last `reset(values)` installed, because
    // RHF adopts those as the new defaults.
    reset(defaultValues);
    customFields.resetFields();
    dispatch(toggleCandidateEditorSidesheet({ status: false }));
    onClose();
  }

  const detailsEditable = useMemo(() => {
    if (!candidate) return true;

    return [OrganizationCandidateType.UNLINKED].includes(candidate.type);
  }, [candidate]);

  const selectableLabels = useMemo(() => {
    if (!availableLabels?.results) return [];

    return availableLabels.results.map((label) => ({
      label: label.label as string,
      value: label.id as string,
    }));
  }, [availableLabels]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 100,
      }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, backgroundColor: 'white !important' } }}
    >
      <Box sx={{ px: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>

        <CardHeader
          title={candidate ? t('candidates.editor-sidesheet.edit.title') : t('candidates.editor-sidesheet.create.title')}
          subheader={t('candidates.editor-sidesheet.form.subheader')}
          subheaderTypographyProps={{ variant: 'caption', mb: 2 }}
        />

        <Divider />
        <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <FormProvider methods={methods} onSubmit={onSubmit}>
            <Grid container gap={2}>


              {
                errorMessage && (
                  <Grid xs={12}>
                    <Alert variant='outlined' severity='error'>
                      {errorMessage}
                    </Alert>
                  </Grid>
                )
              }

              <Grid xs={12}>
                <RHFTextField
                  name="first_name"
                  disabled={!detailsEditable}
                  label={t('common.form_labels.first_name')}
                  placeholder={t('common.placeholders.first_name')}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="last_name"
                  disabled={!detailsEditable}
                  label={t('common.form_labels.last_name')}
                  placeholder={t('common.placeholders.last_name')}
                />
              </Grid>

              <Grid xs={12} sm={12}>
                <RHFTextField
                  name="email"
                  disabled={candidate ? true : !detailsEditable}
                  label={t('common.form_labels.email')}
                  placeholder={t('common.placeholders.email')}
                />
              </Grid>

              <Grid xs={12} sm={12}>
                <RHFTextField
                  name="phone_number"
                  disabled={!detailsEditable}
                  label={t('account.profile.form.phone.label')}
                  placeholder={t('account.profile.form.phone.placeholder')}
                />
              </Grid>

              <Grid xs={12} sm={12}>
                <RHFTextField
                  name="position_title"
                  disabled={!detailsEditable}
                  label={t('common.form_labels.position_title')}
                  placeholder={t('common.placeholders.position_title')}
                />
              </Grid>

              <Grid xs={12} sm={12}>
                <RHFTextField
                  name="special_information"
                  label={t('candidates.editor-sidesheet.form.special_information.label')}
                  placeholder={t('candidates.editor-sidesheet.form.special_information.placeholder')}
                  multiline
                  minRows={2}
                  maxRows={10}
                />
              </Grid>

              <Grid xs={12}>

                <RHFAutocomplete
                  name="labels"
                  multiple
                  label={t('candidates.editor-sidesheet.form.labels.label')}
                  placeholder={t('candidates.editor-sidesheet.form.labels.placeholder')}
                  options={selectableLabels}
                  renderOption={({ key, ...rest }, option: { label: string, value: string }) => (
                    <li {...rest} key={option.value}>
                      <Stack direction="row" justifyContent="space-between" width="100%">
                        <Typography variant="subtitle2">{option.label}</Typography>
                      </Stack>
                    </li>
                  )}
                  onInputChange={(_e, value) => onSearchQueryChange(value)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                />
              </Grid>

              {/* Custom Fields */}
              {customFields.hasCustomFields && (
                <>
                  {customFields.renderCustomFields}
                </>
              )}

              <Grid xs={12} sm={12}>

                <Tooltip
                  title={canAccess(FeatureType.CONNECT_CANDIDATES_LIMIT) ? '' : t('candidates.editor-sidesheet.form.create_invite.locked_tooltip')}
                  placement="top"
                >
                  <RHFCheckbox
                    name="create_invite"
                    disabled={!detailsEditable && !canAccess(FeatureType.CONNECT_CANDIDATES_LIMIT)}
                    label={t('candidates.editor-sidesheet.form.create_invite.label')}
                  />

                </Tooltip>

              </Grid>

            </Grid>
          </FormProvider>
        </CardContent>
        <Divider />
        <CardActions>
          <Stack direction="row" spacing={2} sx={{ width: '100%', my: 2 }} justifyContent="flex-end">
            <LoadingButton
              variant="outlined"
              onClick={handleClose}
            >
              {t('common.cancel')}
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              onClick={onSubmit}
              loading={createLocalCandidateLoading}
            >
              {candidate ? t('candidates.editor-sidesheet.form.update') : t('candidates.editor-sidesheet.form.create')}
            </LoadingButton>
          </Stack>
        </CardActions>
      </Box>
    </Drawer >
  );
}